// src/components/groups/GroupHomePanel.jsx
//
// Shared, presentational Group-home panel (Groups Tier 2). It is the SINGLE body of the group
// home surface, mounted by BOTH the mobile Home tab (bare) and the desktop GroupHomeColumn rail
// (inside its ModeColumn shell — see GroupHomeColumn). Same content everywhere; the rail adds
// only its outer chrome (pinned footer/branding, membership modals). One deliberate step of
// navigation only: it shows the group identity + the parent ("Part of") one level up and the
// immediate sub-groups one level down. Tapping a row NAVIGATES via onGroupSelect (which passes
// through the §4 gate) — there is NO inline drill / accordion expansion.
//
// Data + actions come entirely from `home` (a useGroupHome return) + the `group` prop, exactly
// as GroupHomeColumn consumes them — no fetches, no service calls here.
//
// MODALS: deliberately NOT rendered here. The membership buttons trigger the shared `home`
// flags (showJoinModal / showShareModal / showLeaveConfirmation); the three membership modals
// are rendered ONCE by the owner of the `home` instance — MobileGroupFrontPage on mobile,
// GroupHomeColumn on desktop — so the Home tab and the rail never double-mount them.

import React from 'react';
import { Lock, Share2 } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import { groupTypeLabelKey } from '../../utils/groupHelpers';

const TEAL = '#4ECDC4';

const GroupHomePanel = ({ group, home, onGroupSelect }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('groups');
  const mauve = theme?.primary || tokens.colors.contextGroups;

  const {
    isLoggedIn,
    membershipStatus: ms = {},
    childGroups = [],
    federationMembers = [],
    parentGroup,
    members = [],
    hasLandingPage,
    handleJoinClick,
    handleCancelRequest,
    handleLeaveClick,
    joinLoading,
    joinMessage,
    setShowShareModal,
    shareInfo,
    shareCopied,
    handleShareGroup,
    federationApplyEligibility: feApply = {},
    handleFederationApplyClick,
    federationApplyMessage,
    federationApplySuccess
  } = home || {};

  if (!group) return null;

  // A federation's headline stat counts MEMBER GROUPS, not people — group.member_count is the
  // denormalised people-count (incremented only by accept_join_request/accept_invitation;
  // accept_federation_group never touches it), so it stays fixed at the federation's own
  // admin/owner headcount regardless of how many groups federate. Sourced from federationMembers
  // — the SAME deleted_at-filtered array the FEDERATION MEMBERS section below renders — not a
  // parallel count, so the number and the list can never disagree.
  const totalMembers = group.is_federation
    ? federationMembers.length
    : (group.member_count ?? members.length);
  const membersStatLabel = group.is_federation ? t('meta_member_groups') : t('meta_members');
  const visibilityLabel = t(groupTypeLabelKey(group));

  const openIntro = () => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'group-landing-view', group }
    }));
  };

  // --- shared bits (mirror GroupHomeColumn) -------------------------------
  const sectionHeader = (label) => (
    <div style={{
      fontSize: '13px', fontWeight: 'bold', color: mauve,
      textTransform: 'uppercase', letterSpacing: '0.4px',
      padding: '14px 6px 6px', borderTop: '1px solid #eceff4', marginTop: '10px'
    }}>
      {label}
    </div>
  );

  // A navigable hierarchy row (sub-group / federation member / parent up-link). Tapping it
  // NAVIGATES to that group via onGroupSelect — the §4 gate then opens its Home tab (public)
  // or its landing page (private + non-member). Private rows carry a padlock + "· private"
  // hint so they read as request-to-enter, not a dead end.
  const navRow = (item, { dotColor, actionLabel, actionColor }) => {
    const isPrivate = !!item.is_private;
    return (
      <div
        key={item.id}
        onClick={() => onGroupSelect?.(item)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 8px', borderRadius: '6px', cursor: 'pointer',
          fontSize: '14px', minHeight: '24px',
          fontFamily: tokens.typography.fontFamily, color: '#1A1A1A',
          transition: 'background 0.15s'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f2f6'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
      >
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        <span style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '5px',
          overflow: 'hidden'
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.name}
          </span>
          {isPrivate && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px', flexShrink: 0,
              fontSize: '11px', color: tokens.colors.textSecondary
            }}>
              <Lock size={11} strokeWidth={2.5} />
              · {t(groupTypeLabelKey(item))}
            </span>
          )}
          {/* A closing sub-group, listed only to its own members (Roadmap #103, addendum 2 ruling 2). */}
          {item.is_closing && (
            <span style={{ flexShrink: 0, fontSize: '11px', color: tokens.colors.notificationAlert }}>
              · {item.deletion_scheduled_at
                ? t('closing_date', { date: new Date(item.deletion_scheduled_at).toLocaleDateString(formattingLocale) })
                : t('closing_notice_undated')}
            </span>
          )}
        </span>
        <span style={{ fontSize: '11px', fontWeight: 'bold', color: actionColor, textTransform: 'uppercase', flexShrink: 0 }}>
          {actionLabel} ›
        </span>
      </div>
    );
  };

  // --- membership control (state machine → right-aligned third-width row) ---
  //
  // Layout (Part 3): the control is a single right-aligned row. Each *button* is ~1/3 of the
  // panel/column width; when there are two (member: action + Invite; owner/pending: status +
  // action) they sit on the same line, right-aligned together, leaving the left third empty.
  // Status elements (Owner badge, Requested hint) size to their content — only the real button
  // beside them is the third-width one. Primary action filled (teal), secondary outlined.
  //
  // Labels (Part 2) are driven by group.auto_accept_members, not is_private (the two are
  // independent — see DATABASE.md § group_join_requests): an approval-required group (any
  // privacy) says Request to Join; an auto-accept group says Follow (public) / Join (private).
  // Only the wording differs — the join/leave/request handlers are unchanged ("Following" calls
  // the same leave handler). Mirrors GroupPrivateGate.jsx's identical autoAccept branch.
  const THIRD = '33.333%';

  const actionRow = (children) => (
    <div style={{
      display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
      gap: '8px', width: '100%', marginTop: '10px'
    }}>
      {children}
    </div>
  );

  const thirdButton = (label, onClick, { variant = 'primary', disabled = false } = {}) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: THIRD, height: '40px', borderRadius: '20px', boxSizing: 'border-box',
        background: variant === 'primary' ? TEAL : '#fff',
        color: variant === 'primary' ? '#fff' : TEAL,
        border: variant === 'primary' ? 'none' : `2px solid ${TEAL}`,
        fontWeight: 'bold', fontSize: '13.5px',
        cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.7 : 1,
        fontFamily: tokens.typography.fontFamily,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
      }}
    >
      {label}
    </button>
  );

  // Content-sized status pill (Owner badge / Requested hint) — NOT forced to a third.
  const statusPill = (label, bg) => (
    <div style={{
      height: '40px', borderRadius: '20px', background: bg, color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
      padding: '0 16px', fontWeight: 'bold', fontSize: '13.5px',
      fontFamily: tokens.typography.fontFamily, whiteSpace: 'nowrap'
    }}>
      {label}
    </div>
  );

  // Member-facing Share — content-sized (like statusPill), not a forced THIRD: it isn't
  // competing with Invite/Follow for the row's primary-action width, it's a secondary utility
  // sitting beside it. Shown to any member (not owner-gated, unlike Invite) whenever the
  // resolver seam (getGroupShareUrl) says the group is shareable — public groups only today.
  const shareButton = () => (
    <button
      onClick={handleShareGroup}
      style={{
        height: '40px', borderRadius: '20px', boxSizing: 'border-box',
        background: '#fff', color: mauve, border: `2px solid ${mauve}`,
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '0 14px', fontWeight: 'bold', fontSize: '13.5px', cursor: 'pointer',
        fontFamily: tokens.typography.fontFamily, whiteSpace: 'nowrap'
      }}
    >
      <Share2 size={15} strokeWidth={2.5} />
      {shareCopied ? t('link_copied') : t('share_group')}
    </button>
  );

  const renderMembership = () => {
    if (ms.loading) return null;
    // A federation federates groups, not people (Michael's ruling, 12 Sep; Architect's ruling
    // settling it: suppress the offer, don't constrain membership at the DB). group here is
    // completeGroup (useGroupHome.js), never a card-narrow projection.
    if (group.is_federation) return null;

    // Not a member (visitor or logged-in non-member): Request to Join (approval-required,
    // any privacy) / Follow (public, auto-accept) / Join (private, auto-accept).
    const autoAccept = group.auto_accept_members !== false;
    const notMemberLabel = autoAccept
      ? (group.is_private ? t('ui:join') : t('follow'))
      : t('request_to_join');

    if (!isLoggedIn) {
      return actionRow(thirdButton(notMemberLabel, handleJoinClick));
    }
    // Smart-link Invite is admin-only (Smart Links Phase 2, S3) and never offered for a
    // private group (S5) — is_community_admin()/is_community_private() enforce both server-side.
    // community_members.role only ever holds 'member' or 'owner' in this codebase (DATABASE.md
    // § communities Seeding Note; nothing here ever writes 'admin' — checkMembership just passes
    // the column straight through) — so among non-owner members there is currently no one this
    // button could correctly be offered to. It's dropped from that branch entirely rather than
    // gated on a role value that can't occur; owner already gets it in the branch above.
    const canInvite = !group.is_private;
    if (ms.isMember && ms.isOwner) {
      return actionRow(
        <>
          {statusPill(`✓ ${t('ui:owner_badge')}`, '#5DBEA3')}
          {shareInfo?.isShareable && shareButton()}
          {canInvite && thirdButton(t('ui:invite'), () => setShowShareModal?.(true), { variant: 'primary' })}
        </>
      );
    }
    if (ms.isMember) {
      // Member: Following (public) / Leave (private) — both call the existing leave handler.
      const memberLabel = joinLoading
        ? t('ui:leaving')
        : (group.is_private ? t('ui:leave') : t('following'));
      return actionRow(
        <>
          {shareInfo?.isShareable && shareButton()}
          {thirdButton(memberLabel, handleLeaveClick, { variant: 'primary', disabled: joinLoading })}
        </>
      );
    }
    if (ms.hasPendingRequest) {
      // Pending applies to any approval-required group, private or public: Requested status + Cancel.
      return actionRow(
        <>
          {statusPill(t('ui:request_pending'), '#F59E0B')}
          {thirdButton(t('common:cancel'), handleCancelRequest, { variant: 'secondary', disabled: joinLoading })}
        </>
      );
    }
    return actionRow(
      thirdButton(joinLoading ? t('ui:joining') : notMemberLabel, handleJoinClick, { disabled: joinLoading })
    );
  };

  // --- federation-apply control (Federation 4B §1) -------------------------
  // A DISTINCT relationship from renderMembership(): whether one of the viewer's OTHER
  // (top-level, not-already-federated) groups can apply to federate with THIS group. Only
  // ever shown on a group that is itself a federation. Three states: nothing (no eligible
  // group), a disabled "pending" pill (every eligible group already has a request in against
  // this target), or the Apply button (opens the group-picker/form modal).
  //
  // Own row + own button (UI polish brief, 13 Sep) — deliberately NOT actionRow/thirdButton.
  // Those are shared with renderMembership()'s ordinary-group buttons; thirdButton's fixed
  // 33.333%-width + ellipsis truncated "Apply to Join" ("Apply to Jo…") on the live site.
  // Widening thirdButton itself would restyle every ordinary group page's Join/Follow/Request
  // button too, which the brief explicitly forbids — branched instead, federation-only.
  const federationActionRow = (children) => (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      width: '100%', marginTop: '10px'
    }}>
      {children}
    </div>
  );

  // maxWidth + whiteSpace:'normal' (Advisor catch, pre-commit consult on this build): fits the
  // known-live strings on one line ("Apply to Join" / "Candidatar-se", both checked), but never
  // trusts that — the panel's own scroll container is overflowX:'hidden' (clips, doesn't
  // reflow), so an unbounded auto-width button would silently reproduce the exact truncation
  // this control exists to fix the moment either string gets longer. Wraps to a second line
  // instead of clipping if it ever doesn't fit.
  const federationApplyButton = (label, onClick, { disabled = false } = {}) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        minHeight: '40px', maxWidth: '100%', borderRadius: '20px', boxSizing: 'border-box',
        background: TEAL, color: '#fff', border: 'none',
        padding: '10px 24px', fontWeight: 'bold', fontSize: '13.5px', lineHeight: '1.3',
        cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.7 : 1,
        fontFamily: tokens.typography.fontFamily, whiteSpace: 'normal', textAlign: 'center'
      }}
    >
      {label}
    </button>
  );

  const renderFederationApply = () => {
    if (!group.is_federation || feApply.loading) return null;
    const eligibleGroups = feApply.eligibleGroups || [];
    const applicableGroups = feApply.applicableGroups || [];
    if (eligibleGroups.length === 0) return null;

    if (applicableGroups.length === 0) {
      return federationActionRow(statusPill(t('federation_apply_pending'), '#F59E0B'));
    }
    return federationActionRow(
      federationApplyButton(t('federation_apply_button'), handleFederationApplyClick)
    );
  };

  // --- render -------------------------------------------------------------
  return (
    <div style={{
      height: '100%', overflowY: 'auto', overflowX: 'hidden',
      padding: '12px 12px 24px', boxSizing: 'border-box',
      fontFamily: tokens.typography.fontFamily, background: '#fff'
    }}>
      {/* IDENTITY */}
      <div style={{ padding: '4px 4px 10px', textAlign: 'center' }}>
        <div style={{
          width: '62%', maxWidth: '400px', aspectRatio: '4 / 3', margin: '0 auto 8px', borderRadius: '8px',
          overflow: 'hidden', background: `linear-gradient(135deg, ${mauve}cc, ${mauve})`,
          display: 'flex', alignItems: 'flex-end'
        }}>
          {group.logo_url ? (
            <img
              src={group.logo_url}
              alt={group.name}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div style={{
              width: '100%', padding: '8px', color: '#fff', fontWeight: 'bold',
              fontSize: '15px', textAlign: 'left',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.45))'
            }}>
              {group.name}
            </div>
          )}
        </div>

        <div style={{ fontSize: '17px', fontWeight: 'bold', color: mauve, margin: '2px 0 6px' }}>
          {group.name}
        </div>

        {/* Federation apply control — immediately below the name, centred (UI polish brief,
            13 Sep, Michael's exact placement). Federation-only; renderMembership() already
            returns null on a federation page, so this can't collide with the ordinary
            Join/Follow/Request-to-Join row below. */}
        {federationApplyMessage && (
          <div style={{
            margin: '4px 6px 0', padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
            color: '#fff', textAlign: 'center',
            background: federationApplySuccess ? '#5DBEA3' : '#EF4444'
          }}>
            {federationApplyMessage}
          </div>
        )}

        {renderFederationApply()}

        {group.description && (
          <div style={{ fontSize: '12.5px', color: '#6b7280', lineHeight: '1.5', padding: '0 6px' }}>
            {group.description}
            {/* "read the full intro" — only when a published landing page exists to open to */}
            {hasLandingPage && (
              <>
                {' '}
                <span
                  onClick={openIntro}
                  style={{ color: mauve, fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('read_full_intro')} ›
                </span>
              </>
            )}
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'center', gap: '14px',
          marginTop: '8px', fontSize: '12px', color: '#6b7280'
        }}>
          <div><b style={{ display: 'block', color: '#1A1A1A', fontSize: '14px' }}>{totalMembers}</b>{membersStatLabel}</div>
          <div><b style={{ display: 'block', color: '#1A1A1A', fontSize: '14px' }}>{childGroups.length}</b>{t('meta_subgroups')}</div>
          <div><b style={{ display: 'block', color: '#1A1A1A', fontSize: '14px' }}>{visibilityLabel}</b>{t('meta_visibility')}</div>
        </div>

        {joinMessage && (
          <div style={{
            margin: '8px 6px 0', padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
            color: '#fff', textAlign: 'center',
            background: (joinMessage.includes('joined') || joinMessage.includes('submitted') || joinMessage.includes('left')) ? '#5DBEA3' : '#EF4444'
          }}>
            {joinMessage}
          </div>
        )}

        {renderMembership()}
      </div>

      {/* SUB-GROUPS (navigate, one level down — no drill) */}
      {childGroups.length > 0 && (
        <>
          {sectionHeader(t('section_subgroups'))}
          {childGroups.map(child => navRow(child, {
            dotColor: mauve,
            actionLabel: t('enter_subgroup'),
            actionColor: mauve
          }))}
          <div style={{
            fontSize: '11.5px', color: tokens.colors.textSecondary, lineHeight: '1.5',
            padding: '8px 6px 0'
          }}>
            {t('home_subgroup_hint')}
          </div>
        </>
      )}

      {/* FEDERATION MEMBERS */}
      {federationMembers.length > 0 && (
        <>
          {sectionHeader(t('federation_members'))}
          {federationMembers.map(member => navRow(member, {
            dotColor: mauve,
            actionLabel: t('enter_subgroup'),
            actionColor: mauve
          }))}
        </>
      )}

      {/* PART OF (navigate, one level up — gated identically to sub-groups). Nested parent only.
          A sub-group SITS INSIDE its parent — "Part of" is exactly right for that. A federation
          membership is a peer relationship, not containment (Rede Bio does not own Horta da
          Serra; the farm is a member of a network of equals), so it doesn't share this block or
          this header — it lives in its own pinned foot-of-column slot instead, rendered by
          GroupHomeColumn (desktop footer) and MobileGroupFrontPage (mobile Home tab), never by
          this component (Michael's ruling, 13 Sep, after seeing the two claims sharing one header
          live, then a placement revision once the shared block's occupant turned out not to be
          reliably pinned — see FederationMembershipStatement.jsx). Splitting satisfies the
          both-lines-tolerant requirement (Federation 4D, design note §3) more completely than
          sharing a header did: this line and the federation statement no longer compete for one
          header, and a future sub-group that joins a federation gets both — this line AND the
          statement, rendered independently, no interaction between them. */}
      {parentGroup && (
        <>
          {sectionHeader(t('section_part_of'))}
          {navRow(parentGroup, {
            dotColor: mauve,
            actionLabel: t('go_up'),
            actionColor: mauve
          })}
        </>
      )}
    </div>
  );
};

export default GroupHomePanel;
